/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * BLOCO COMERCIAL 7C — Sincronização da Capacidade após Troca do Ciclo Operacional.
 * Determina o limite do plano via CommercialPlanService.getPlanCourtLimit(...) e sincroniza
 * o estado operacional das quadras da Arena usando a ordenação determinística e o writer canônico do Bloco 6.
 */

import { collection, query, where, getDocs } from "firebase/firestore";
import { db, isFirebaseConfigured, updateCourtPartialFirestore } from "../../utils/firebase";
import { CourtItem } from "../../types/userProfile";
import { CommercialPlanService } from "./CommercialPlanService";
import { DEFAULT_COMMERCIAL_PLANS } from "../../utils/commissionConfig";
import { isCourtOperationallyActive } from "../arenaVerification/arenaVisibilityService";
import { StorageService, STORAGE_KEYS } from "../storage";
import { resolveArenaFinancialModelSync } from "./FinancialModelResolver";

export interface OperationalCapacitySyncResult {
  success: boolean;
  arenaId: string;
  model: string;
  planId?: string;
  limit: number | "unlimited";
  totalCourts: number;
  eligibleCourtsCount: number;
  deactivatedCourtIds: string[];
  reactivatedCourtIds: string[];
  unchangedCourtIds: string[];
}

/**
 * Sincroniza a capacidade operacional das quadras da Arena com o ciclo comercial operacional.
 * 
 * A sequência arquitetural do Bloco 7 é:
 * resolveOperationalCommercialCycle
 *         ↓
 * applyOperationalCommercialCycle
 *         ↓
 * resolve limite do plano
 *         ↓
 * sincroniza quadras
 */
export async function syncOperationalCourtCapacity(
  arenaId: string,
  optionsOrModel?:
    | string
    | number
    | {
        model?: string;
        planId?: string;
        resolution?: any;
        firestoreDb?: any;
        courts?: CourtItem[];
        limit?: number | "unlimited";
      },
  planIdOrDb?: string | any,
  maybeDb?: any
): Promise<OperationalCapacitySyncResult> {
  if (!arenaId || typeof arenaId !== "string" || !arenaId.trim()) {
    throw new Error("arenaId é obrigatório para sincronizar capacidade de quadras.");
  }

  let model = "";
  let planId: string | undefined = undefined;
  let firestoreDb: any = undefined;
  let customCourts: CourtItem[] | undefined = undefined;
  let directLimit: number | "unlimited" | undefined = undefined;

  if (typeof optionsOrModel === "object" && optionsOrModel !== null) {
    if (optionsOrModel.resolution) {
      model = optionsOrModel.resolution.model;
      planId = optionsOrModel.resolution.planId;
    } else {
      model = optionsOrModel.model || "";
      planId = optionsOrModel.planId;
    }
    firestoreDb = optionsOrModel.firestoreDb || (typeof planIdOrDb === "object" ? planIdOrDb : undefined);
    customCourts = optionsOrModel.courts;
    directLimit = optionsOrModel.limit;
  } else if (typeof optionsOrModel === "number") {
    directLimit = optionsOrModel;
    firestoreDb = typeof planIdOrDb === "object" ? planIdOrDb : maybeDb;
  } else if (typeof optionsOrModel === "string") {
    if (optionsOrModel === "unlimited") {
      directLimit = "unlimited";
      firestoreDb = typeof planIdOrDb === "object" ? planIdOrDb : maybeDb;
    } else {
      model = optionsOrModel;
      if (typeof planIdOrDb === "string") {
        planId = planIdOrDb;
        firestoreDb = maybeDb;
      } else if (typeof planIdOrDb === "object") {
        firestoreDb = planIdOrDb;
      }
    }
  }

  // Se o modelo não foi passado explicitamente e nem o limite direto, busca o modelo aplicado da arena
  if (!model && directLimit === undefined) {
    const storedConfigs = StorageService.get<Record<string, any>>("app_arena_financial_configs", {}) || {};
    const currentStored = storedConfigs[arenaId];
    if (currentStored) {
      model = currentStored.financialModel;
      planId = planId || currentStored.commercialPlanId;
    } else {
      const currentConfig = resolveArenaFinancialModelSync({ arenaId, arenaData: {} });
      model = currentConfig.model;
      planId = planId || (currentConfig as any).commercialPlanId;
    }
  }

  // 1. Resolve o limite do plano exclusivamente através de CommercialPlanService.getPlanCourtLimit(...)
  let limit: number | "unlimited";
  if (directLimit !== undefined) {
    limit = directLimit;
  } else {
    let plan: any = null;
    if (model === "SPLIT") {
      plan = { id: planId || "split_1_open", financialModel: "SPLIT", maxCourts: 0 };
    } else if (planId) {
      plan =
        CommercialPlanService.getPlanById(planId) ||
        DEFAULT_COMMERCIAL_PLANS.find((p) => p.id === planId) ||
        null;
    }
    limit = CommercialPlanService.getPlanCourtLimit(plan);
  }

  // 2. Obtém as quadras pertencentes à Arena
  let courts: CourtItem[] = [];
  if (customCourts && Array.isArray(customCourts)) {
    courts = [...customCourts];
  } else {
    const dbInstance = firestoreDb || db;
    if (isFirebaseConfigured() && dbInstance) {
      try {
        const courtsRef = collection(dbInstance, "courts");
        const q = query(courtsRef, where("arenaId", "==", arenaId));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((docSnap) => {
          courts.push({ id: docSnap.id, ...(docSnap.data() as any) });
        });
      } catch {
        // query fallback para ambiente mockado ou storage local
      }
    }

    if (courts.length === 0) {
      const cached = StorageService.get<CourtItem[]>(STORAGE_KEYS.RACHAO_COURTS, []) || [];
      courts = cached.filter((c) => c.arenaId === arenaId || (c as any).canonicalArenaId === arenaId);
    }
  }

  // 3. SELEÇÃO DETERMINÍSTICA (Reutiliza EXATAMENTE a regra homologada no Bloco 6):
  //    1. quadras já ativas primeiro (a.isActive || a.active);
  //    2. createdAt;
  //    3. id/courtId como desempate.
  courts.sort((a, b) => {
    const aActive = a.isActive || a.active ? 1 : 0;
    const bActive = b.isActive || b.active ? 1 : 0;
    if (aActive !== bActive) {
      return bActive - aActive; // Ativos primeiro
    }
    return (a.createdAt || 0) - (b.createdAt || 0) || a.id.localeCompare(b.id);
  });

  const deactivatedCourtIds: string[] = [];
  const reactivatedCourtIds: string[] = [];
  const unchangedCourtIds: string[] = [];

  if (limit === "unlimited") {
    // 4. SPLIT / UNLIMITED:
    //    NÃO desativa nenhuma quadra por limite.
    //    SOMENTE quadras com status comprovado "INACTIVE_COMMERCIAL_LIMIT" podem ser recuperadas.
    //    NÃO reativa quadras bloqueadas, suspensas, rejeitadas, pendentes ou inativas manuais.
    for (const court of courts) {
      const rawStatus = (court.status || "").toString().toUpperCase().trim();
      if (rawStatus === "INACTIVE_COMMERCIAL_LIMIT") {
        await updateCourtPartialFirestore(court.id, {
          isActive: true,
          active: true,
          status: "ACTIVE",
        });
        court.isActive = true;
        court.active = true;
        court.status = "ACTIVE";
        reactivatedCourtIds.push(court.id);
      } else {
        unchangedCourtIds.push(court.id);
      }
    }
  } else {
    // 5. FIXED_MONTHLY / HYBRID com limite numérico N:
    const allowedLimit = limit;

    // Primeiras allowedLimit quadras: elegíveis/operacionais
    for (let i = 0; i < Math.min(allowedLimit, courts.length); i++) {
      const court = courts[i];
      const rawStatus = (court.status || "").toString().toUpperCase().trim();

      if (rawStatus === "INACTIVE_COMMERCIAL_LIMIT") {
        // Quadra antes desativada por limite comercial que agora cabe no novo limite
        await updateCourtPartialFirestore(court.id, {
          isActive: true,
          active: true,
          status: "ACTIVE",
        });
        court.isActive = true;
        court.active = true;
        court.status = "ACTIVE";
        reactivatedCourtIds.push(court.id);
      } else {
        unchangedCourtIds.push(court.id);
      }
    }

    // Quadras excedentes (além de allowedLimit): desativação por limite comercial
    for (let i = allowedLimit; i < courts.length; i++) {
      const court = courts[i];
      const rawStatus = (court.status || "").toString().toUpperCase().trim();

      if (isCourtOperationallyActive(court)) {
        await updateCourtPartialFirestore(court.id, {
          isActive: false,
          active: false,
          status: "INACTIVE_COMMERCIAL_LIMIT",
        });
        court.isActive = false;
        court.active = false;
        court.status = "INACTIVE_COMMERCIAL_LIMIT";
        deactivatedCourtIds.push(court.id);
      } else {
        // Já está inativa (por limite comercial anterior ou outro motivo) -> idempotente
        unchangedCourtIds.push(court.id);
      }
    }
  }

  const eligibleCourtsCount = courts.filter((c) => isCourtOperationallyActive(c)).length;

  return {
    success: true,
    arenaId,
    model: model || (limit === "unlimited" ? "SPLIT" : "FIXED_MONTHLY"),
    planId,
    limit,
    totalCourts: courts.length,
    eligibleCourtsCount,
    deactivatedCourtIds,
    reactivatedCourtIds,
    unchangedCourtIds,
  };
}
